import { createDirectoryHandleFs } from "./directoryHandleFs.js";
import { GIT_DEFAULT_REMOTE, GIT_GITDIR, GIT_HTTP_CORS_PROXY, GIT_WORKDIR } from "./gitConstants.js";
import { statusMatrixWithWorkerFallback, unstageAllWithWorkerFallback } from "./isoGitWorkerClient.js";
import { getLargestPackfileBytes } from "./workspacePackStats.js";

const STATUS_SCAN_STEP_TIMEOUT_MS = 12_000;

export type GitNetworkProgress = {
  phase?: string;
  loaded?: number;
  total?: number;
  message?: string;
};

async function readAuthorCommitter(fs: ReturnType<typeof createDirectoryHandleFs>): Promise<{
  author: { name: string; email: string };
  committer: { name: string; email: string };
}> {
  const git = await import("isomorphic-git");
  let name: unknown;
  let email: unknown;
  try {
    name = await git.getConfig({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, path: "user.name" });
  } catch {
    name = "";
  }
  try {
    email = await git.getConfig({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, path: "user.email" });
  } catch {
    email = "";
  }
  const n = String(name ?? "").trim() || "npp-web";
  const e = String(email ?? "").trim() || "npp-web@local.workspace";
  const author = { name: n, email: e };
  return { author, committer: { ...author } };
}

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      p,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`timeout after ${Math.round(ms / 1000)}s`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function toStatusCode(head: number, workdir: number, stage: number): string {
  // `??` = untracked (only shown when `-u` full scan is used).
  if (head === 0 && stage === 0 && workdir === 2) return "??"
  // Staged add/delete/modify.
  if (head === 0 && stage === 2) return "A "
  if (head === 1 && stage === 0) return "D "
  if (head === 1 && stage === 2) return "M "
  // Unstaged delete/modify.
  if (head === 1 && stage === 1 && workdir === 0) return " D"
  if (head === 1 && stage === 1 && workdir === 2) return " M"
  // Mixed/conflict-ish fallback.
  if (stage === 3) return "UU"
  return "MM"
}

export async function gitWorkspaceFetch(
  root: FileSystemDirectoryHandle,
  onProgress?: (p: GitNetworkProgress) => void,
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const [{ fetch }, httpMod] = await Promise.all([
    import("isomorphic-git"),
    import("isomorphic-git/http/web"),
  ]);
  await fetch({
    fs,
    http: httpMod.default,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    remote: GIT_DEFAULT_REMOTE,
    corsProxy: GIT_HTTP_CORS_PROXY,
    singleBranch: false,
    prune: false,
    tags: false,
    onProgress: (e: unknown) => onProgress?.(e as GitNetworkProgress),
    onMessage: (message: string) => onProgress?.({ message }),
  });
}

async function isDirectoryEmpty(root: FileSystemDirectoryHandle): Promise<boolean> {
  for await (const _ of root.entries()) {
    return false
  }
  return true
}

export async function gitWorkspaceCloneInto(
  root: FileSystemDirectoryHandle,
  url: string,
  onProgress?: (p: GitNetworkProgress) => void,
): Promise<void> {
  const repoUrl = String(url ?? "").trim();
  if (!repoUrl) throw new Error("usage: git clone <url> [directory]");
  if (!(await isDirectoryEmpty(root))) {
    throw new Error("Clone target folder is not empty. Pick an empty folder.");
  }
  const fs = createDirectoryHandleFs(root);
  const [{ clone }, httpMod] = await Promise.all([
    import("isomorphic-git"),
    import("isomorphic-git/http/web"),
  ]);
  await clone({
    fs,
    http: httpMod.default,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    url: repoUrl,
    singleBranch: false,
    corsProxy: GIT_HTTP_CORS_PROXY,
    onProgress: (e: unknown) => onProgress?.(e as GitNetworkProgress),
    onMessage: (message: string) => onProgress?.({ message }),
  });
}

export async function gitWorkspacePull(
  root: FileSystemDirectoryHandle,
  onProgress?: (p: GitNetworkProgress) => void,
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const [{ pull }, httpMod] = await Promise.all([
    import("isomorphic-git"),
    import("isomorphic-git/http/web"),
  ]);
  const { author, committer } = await readAuthorCommitter(fs);
  await pull({
    fs,
    http: httpMod.default,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    remote: GIT_DEFAULT_REMOTE,
    fastForward: true,
    fastForwardOnly: false,
    corsProxy: GIT_HTTP_CORS_PROXY,
    author,
    committer,
    onProgress: (e: unknown) => onProgress?.(e as GitNetworkProgress),
    onMessage: (message: string) => onProgress?.({ message }),
  });
}

export async function gitWorkspaceListLocalBranches(root: FileSystemDirectoryHandle): Promise<string[]> {
  const fs = createDirectoryHandleFs(root);
  const { listBranches } = await import("isomorphic-git");
  return listBranches({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR });
}

export async function gitWorkspaceListRemoteBranches(root: FileSystemDirectoryHandle): Promise<string[]> {
  const fs = createDirectoryHandleFs(root);
  const { listBranches } = await import("isomorphic-git");
  try {
    return await listBranches({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, remote: GIT_DEFAULT_REMOTE });
  } catch {
    return [];
  }
}

export async function gitWorkspaceCheckoutLocal(root: FileSystemDirectoryHandle, ref: string): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { checkout } = await import("isomorphic-git");
  await checkout({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, ref });
}

export async function gitWorkspaceCheckoutRemote(
  root: FileSystemDirectoryHandle,
  remoteShortRef: string,
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { checkout } = await import("isomorphic-git");
  await checkout({
    fs,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    ref: remoteShortRef,
    remote: GIT_DEFAULT_REMOTE,
  });
}

export async function gitWorkspaceCreateBranch(
  root: FileSystemDirectoryHandle,
  name: string,
  checkout: boolean,
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { branch } = await import("isomorphic-git");
  await branch({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, ref: name, checkout });
}

export async function gitWorkspacePush(
  root: FileSystemDirectoryHandle,
  opts?: { force?: boolean },
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const [{ push }, httpMod] = await Promise.all([
    import("isomorphic-git"),
    import("isomorphic-git/http/web"),
  ]);
  await push({
    fs,
    http: httpMod.default,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    remote: GIT_DEFAULT_REMOTE,
    corsProxy: GIT_HTTP_CORS_PROXY,
    force: Boolean(opts?.force),
  });
}

export async function gitWorkspaceStatusText(
  root: FileSystemDirectoryHandle,
  maxLines = 100,
  opts?: { includeUntracked?: boolean },
): Promise<string> {
  const packBytes = await getLargestPackfileBytes(root);
  const fs = createDirectoryHandleFs(root);
  const { listFiles, statusMatrix } = await import("isomorphic-git");
  let matrix: Array<Array<string | number>> = [];
  let statusNote = "";
  if (opts?.includeUntracked) {
    matrix = await statusMatrixWithWorkerFallback(root, packBytes, async () =>
      withTimeout(statusMatrix({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR }), STATUS_SCAN_STEP_TIMEOUT_MS),
    );
  } else {
    // Fast path for large repos: restrict status walk to tracked files only.
    // If the full tracked set is still too slow, progressively sample fewer paths.
    const tracked = await listFiles({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR });
    const attempts = tracked.length
      ? [tracked, tracked.slice(0, 4000), tracked.slice(0, 1000), tracked.slice(0, 250)]
      : [["."]];
    let got = false;
    let lastErr: unknown;
    for (const paths of attempts) {
      if (!paths.length) continue;
      try {
        matrix = await statusMatrixWithWorkerFallback(root, packBytes, async () =>
          withTimeout(
            statusMatrix({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, filepaths: paths }),
            STATUS_SCAN_STEP_TIMEOUT_MS,
          ),
        );
        got = true;
        if (tracked.length > paths.length) {
          statusNote = `Status partially scanned ${paths.length}/${tracked.length} tracked files for speed.`;
        }
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!got) {
      const msg = lastErr instanceof Error ? lastErr.message : String(lastErr ?? "unknown error");
      return `Status scan is taking too long for this repository in browser mode (${msg}). Try desktop git for full status, or run \`git status -u\` only when needed.`;
    }
  }
  const lines: string[] = [];
  for (const row of matrix) {
    const path = String(row[0]);
    const head = row[1];
    const workdir = row[2];
    const stage = row[3];
    if (head === 1 && workdir === 1 && stage === 1) continue;
    lines.push(`${toStatusCode(Number(head), Number(workdir), Number(stage))} ${path}`);
    if (lines.length >= maxLines) {
      lines.push("… (truncated)");
      break;
    }
  }
  if (lines.length === 0) {
    return opts?.includeUntracked
      ? "Working tree clean (nothing to show)."
      : "Working tree clean (tracked files).";
  }
  if (statusNote) {
    lines.push(statusNote);
  }
  if (!opts?.includeUntracked) {
    lines.push("Note: untracked files omitted for speed. Use `git status -u` for full scan.");
  }
  return lines.join("\n");
}

/**
 * Same scan as `gitWorkspaceStatusText` but returns the raw matrix for tooling (e.g. `git diff`).
 * @throws On timeout / scan failure (same conditions as status text returning an error string).
 */
export async function gitWorkspaceFetchStatusMatrix(
  root: FileSystemDirectoryHandle,
  opts?: { includeUntracked?: boolean },
): Promise<Array<Array<string | number>>> {
  const packBytes = await getLargestPackfileBytes(root);
  const fs = createDirectoryHandleFs(root);
  const { listFiles, statusMatrix } = await import("isomorphic-git");
  let matrix: Array<Array<string | number>> = [];
  if (opts?.includeUntracked) {
    matrix = await statusMatrixWithWorkerFallback(root, packBytes, async () =>
      withTimeout(statusMatrix({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR }), STATUS_SCAN_STEP_TIMEOUT_MS),
    );
    return matrix;
  }
  const tracked = await listFiles({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR });
  const attempts = tracked.length
    ? [tracked, tracked.slice(0, 4000), tracked.slice(0, 1000), tracked.slice(0, 250)]
    : [["."]];
  let lastErr: unknown;
  for (const paths of attempts) {
    if (!paths.length) continue;
    try {
      matrix = await statusMatrixWithWorkerFallback(root, packBytes, async () =>
        withTimeout(
          statusMatrix({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, filepaths: paths }),
          STATUS_SCAN_STEP_TIMEOUT_MS,
        ),
      );
      return matrix;
    } catch (e) {
      lastErr = e;
    }
  }
  const msg = lastErr instanceof Error ? lastErr.message : String(lastErr ?? "unknown error");
  throw new Error(
    `Status scan is taking too long for this repository in browser mode (${msg}). Try desktop git, or use git diff on a single known path.`,
  );
}

export async function gitWorkspaceStageAll(root: FileSystemDirectoryHandle): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { add } = await import("isomorphic-git");
  await add({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, filepath: "." });
}

export async function gitWorkspaceUnstageAll(root: FileSystemDirectoryHandle): Promise<void> {
  const packBytes = await getLargestPackfileBytes(root);
  await unstageAllWithWorkerFallback(root, packBytes, async () => {
    const fs = createDirectoryHandleFs(root);
    const { statusMatrix, resetIndex } = await import("isomorphic-git");
    const matrix = await statusMatrix({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR });
    for (const row of matrix) {
      const filepath = String(row[0]);
      const head = row[1];
      const stage = row[3];
      if (head !== stage) {
        await resetIndex({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, filepath, ref: "HEAD" });
      }
    }
  });
}

export async function gitWorkspaceCommit(root: FileSystemDirectoryHandle, message: string): Promise<string> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  const msg = message.trim();
  if (!msg) throw new Error("Enter a commit message.");
  const { author, committer } = await readAuthorCommitter(fs);
  const oid = await git.commit({
    fs,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    message: msg,
    author,
    committer,
  });
  return String(oid);
}

export async function gitWorkspaceDeleteBranch(root: FileSystemDirectoryHandle, ref: string): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { deleteBranch, currentBranch } = await import("isomorphic-git");
  const cur = await currentBranch({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, fullname: false });
  if (cur === ref) throw new Error("Switch to another branch before deleting this one.");
  await deleteBranch({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, ref });
}

export async function gitWorkspaceRenameBranch(
  root: FileSystemDirectoryHandle,
  oldref: string,
  newName: string,
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { renameBranch } = await import("isomorphic-git");
  const ref = newName.trim().replace(/[/\\]/g, "");
  if (!oldref) throw new Error("Pick a local branch to rename.");
  if (!ref) throw new Error("Enter a new branch name.");
  await renameBranch({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, oldref, ref, checkout: false });
}

export async function gitWorkspaceStashPush(
  root: FileSystemDirectoryHandle,
  message = "",
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { stash } = await import("isomorphic-git");
  await stash({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, op: "push", message });
}

export async function gitWorkspaceStashPop(root: FileSystemDirectoryHandle): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { stash } = await import("isomorphic-git");
  await stash({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, op: "pop", refIdx: 0 });
}

export async function gitWorkspaceStashApply(root: FileSystemDirectoryHandle): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { stash } = await import("isomorphic-git");
  await stash({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, op: "apply", refIdx: 0 });
}

export async function gitWorkspaceStashListText(root: FileSystemDirectoryHandle): Promise<string> {
  const fs = createDirectoryHandleFs(root);
  const { stash } = await import("isomorphic-git");
  const list = await stash({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, op: "list" });
  if (!Array.isArray(list) || list.length === 0) return "(no stashes)";
  return list.map((entry: unknown, i: number) => `${i}: ${String(entry)}`).join("\n");
}

export async function gitWorkspaceCreateTag(
  root: FileSystemDirectoryHandle,
  name: string,
  force = false,
): Promise<void> {
  const fs = createDirectoryHandleFs(root);
  const { tag } = await import("isomorphic-git");
  const ref = name.trim().replace(/^refs\/tags\//, "");
  if (!ref) throw new Error("Enter a tag name.");
  await tag({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, ref, force });
}
